import { useRef, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { MIN_PASSWORD_LENGTH } from '@kontor/shared';
import { api, ApiError } from '../api';
import { useSession } from '../session';
import { AuthBrand } from '../components/AuthBrand';

interface Info {
  tenantName: string;
  tenantNumber: string;
  name: string;
  email: string;
}

interface Texts {
  /** Pfad der API ohne Token, z. B. /auth/invitations */
  endpoint: string;
  brandTitle: string;
  brandText: string;
  unavailableTitle: string;
  unavailableHint: string;
  intro: (info: Info) => string;
  submit: string;
  doneTitle: string;
  done: (info: Info) => string;
}

/**
 * Seite hinter einem Link aus einer E-Mail, auf der jemand ein Passwort festlegt: bei der Einladung das erste, beim
 * Zurücksetzen ein neues. Der Link gilt einmal und läuft ab, beides prüft der Server.
 */
function SetPasswordPage({ texts }: { texts: Texts }) {
  const { token = '' } = useParams();
  const { session, logout } = useSession();
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Wird das Passwort gespeichert, ist der Link verbraucht und darf nicht erneut geprüft werden. Das Abmelden unten
  // verwirft außerdem alle Abfragen außer der Sitzung, deshalb merkt sich die Seite die Angaben zur Einladung selbst.
  const [saving, setSaving] = useState(false);
  const info = useQuery({
    queryKey: [texts.endpoint, token],
    queryFn: async () => (await api<{ data: Info }>('GET', `${texts.endpoint}/${token}`)).data,
    retry: false,
    staleTime: Infinity,
    enabled: !saving,
  });
  const lastInfo = useRef<Info | undefined>(undefined);
  if (info.data) lastInfo.current = info.data;
  const infoData = info.data ?? lastInfo.current;

  const submitPassword = useMutation({
    mutationFn: () => api<{ data: { tenantNumber: string } }>('POST', `${texts.endpoint}/${token}${texts.endpoint.includes('invitations') ? '/accept' : ''}`, { password }),
    onSuccess: async (res) => {
      // Die Mandantennummer merkt sich die Anmeldeseite, damit nur noch E-Mail und Passwort fehlen
      try {
        localStorage.setItem('kontor:tenant', res.data.tenantNumber);
      } catch {
        // Ohne Zugriff auf den lokalen Speicher tippt man die Nummer ein
      }
      // War in diesem Browser schon jemand angemeldet, würde die Anmeldeseite sofort auf dessen Startseite
      // weiterleiten. Die Sitzung wird deshalb beendet, bevor „Zur Anmeldung“ erscheint. Scheitert das, hat das
      // Passwort trotzdem geklappt, deshalb kein Fehler an dieser Stelle.
      if (session) {
        try {
          await logout();
        } catch {
          // Die Anmeldeseite zeigt dann eben die bestehende Sitzung
        }
      }
    },
    onError: (e) => {
      setSaving(false);
      setError(e instanceof ApiError ? (Object.values(e.fields)[0] ?? e.message) : 'Das Passwort konnte nicht gespeichert werden.');
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD_LENGTH) return setError(`Das Passwort braucht mindestens ${MIN_PASSWORD_LENGTH} Zeichen.`);
    if (password !== repeat) return setError('Die beiden Passwörter stimmen nicht überein.');
    setSaving(true);
    submitPassword.mutate();
  }

  return (
    <div className="login">
      <AuthBrand title={texts.brandTitle} text={texts.brandText} />

      <main className="login__main">
        {info.isLoading && <div className="card login__card"><p className="muted">Wird geladen …</p></div>}

        {info.isError && (
          <div className="card login__card">
            <h2>{texts.unavailableTitle}</h2>
            <p>{info.error instanceof ApiError ? info.error.message : 'Der Link konnte nicht geprüft werden.'}</p>
            <p className="muted small">{texts.unavailableHint}</p>
            <Link className="btn btn--secondary" to="/login">Zur Anmeldung</Link>
          </div>
        )}

        {infoData && submitPassword.isSuccess && (
          <div className="card login__card">
            <h2>{texts.doneTitle}</h2>
            <p>{texts.done(infoData)}</p>
            <Link className="btn btn--primary btn--block" to="/login">Zur Anmeldung</Link>
          </div>
        )}

        {infoData && !submitPassword.isSuccess && (
          <form className="card login__card" onSubmit={submit} noValidate>
            <h2>Hallo {infoData.name}</h2>
            <p className="muted">{texts.intro(infoData)}</p>
            {error && <div className="alert alert--error" role="alert">{error}</div>}
            <div className="field">
              <label htmlFor="password">Passwort</label>
              <div className="password">
                <input id="password" className="input" type={show ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" autoFocus />
                <button type="button" className="password__toggle" onClick={() => setShow((v) => !v)} aria-pressed={show} aria-controls="password">
                  {show ? 'Verbergen' : 'Anzeigen'}
                </button>
              </div>
              <small className="muted">Mindestens {MIN_PASSWORD_LENGTH} Zeichen.</small>
            </div>
            <label className="field">
              <span>Passwort wiederholen</span>
              <input className="input" type={show ? 'text' : 'password'} value={repeat} onChange={(e) => setRepeat(e.target.value)} autoComplete="new-password" />
            </label>
            <button className="btn btn--primary btn--block" type="submit" disabled={submitPassword.isPending || !password || !repeat}>
              {submitPassword.isPending ? 'Wird gespeichert …' : texts.submit}
            </button>
          </form>
        )}
      </main>
    </div>
  );
}

const INVITATION: Texts = {
  endpoint: '/auth/invitations',
  brandTitle: 'Willkommen',
  brandText: 'Legen Sie Ihr Passwort fest, danach können Sie sich anmelden.',
  unavailableTitle: 'Einladung nicht verfügbar',
  unavailableHint: 'Bitten Sie die Person, die Sie eingeladen hat, Ihnen einen neuen Link zu geben.',
  intro: (i) => `Sie wurden zu ${i.tenantName} eingeladen. Legen Sie ein Passwort fest, mit dem Sie sich mit ${i.email} anmelden.`,
  submit: 'Passwort festlegen',
  doneTitle: 'Passwort festgelegt',
  done: (i) => `Sie können sich jetzt mit ${i.email} anmelden.`,
};

const RESET: Texts = {
  endpoint: '/auth/password-reset',
  brandTitle: 'Neues Passwort',
  brandText: 'Legen Sie ein neues Passwort fest. Alle bestehenden Anmeldungen enden danach.',
  unavailableTitle: 'Link nicht verfügbar',
  unavailableHint: 'Fordern Sie auf der Anmeldeseite über „Passwort vergessen?“ einen neuen Link an.',
  intro: (i) => `Legen Sie ein neues Passwort für ${i.email} bei ${i.tenantName} fest.`,
  submit: 'Neues Passwort festlegen',
  doneTitle: 'Passwort geändert',
  done: (i) => `Ihr Passwort wurde geändert. Melden Sie sich jetzt mit ${i.email} an. Alle bisherigen Anmeldungen sind beendet.`,
};

export const AcceptInvitationPage = () => <SetPasswordPage texts={INVITATION} />;
export const ResetPasswordPage = () => <SetPasswordPage texts={RESET} />;
